import { LitElement, html, nothing, type CSSResultGroup } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { emit } from '../utils/dom.utils.js';
import styles from './styles.js';
import { FormAssociatedMixin } from '../utils/form-associated.mixin.js';

export type SliderOrientation = 'horizontal' | 'vertical';

/**
 * uwc-slider
 *
 * Range slider with single-value and range (two-handle) modes, horizontal and
 * vertical orientations, full keyboard navigation, and ARIA semantics.
 *
 * @attr {number}              value        - Current value (single mode). Default: 0.
 * @attr {number}              min          - Minimum value. Default: 0.
 * @attr {number}              max          - Maximum value. Default: 100.
 * @attr {number}              step         - Step increment. Default: 1.
 * @attr {'horizontal'|'vertical'} orientation - Layout axis. Default: horizontal.
 * @attr {boolean}             range        - Enable dual-handle range mode.
 * @attr {boolean}             disabled     - Disables all interaction.
 * @attr {boolean}             fluid        - Stretch to container width.
 *
 * @prop {[number,number]}     values       - Range mode start/end pair. Set this
 *                                           instead of `value` when range=true.
 *
 * @fires uwc-change    - On every value change. Detail: `{ value }` (number or
 *                        [number, number] in range mode).
 * @fires uwc-slide-end - On pointerup / key commit. Same detail shape.
 *
 * @csspart handle - Each thumb handle element.
 *
 * @cssprop --uwc-slider-height              - Track thickness. Default: 4px.
 * @cssprop --uwc-slider-bg                  - Track background. Default: border-control token.
 * @cssprop --uwc-slider-range-bg            - Filled-range color. Default: primary token.
 * @cssprop --uwc-slider-border-radius       - Track corner radius. Default: full.
 * @cssprop --uwc-slider-handle-size         - Handle diameter. Default: 20px.
 * @cssprop --uwc-slider-handle-bg           - Handle background. Default: white.
 * @cssprop --uwc-slider-handle-border-color - Handle border color. Default: primary token.
 * @cssprop --uwc-slider-handle-hover-border-color - Handle border on hover.
 * @cssprop --uwc-slider-handle-shadow       - Handle box-shadow.
 * @cssprop --uwc-slider-vertical-height     - Height of vertical slider. Default: 200px.
 */
export class UwcSlider extends FormAssociatedMixin(LitElement) {
  static override styles: CSSResultGroup = [styles];

  // ── Public properties ─────────────────────────────────────────────────────

  /** Single-mode current value. */
  @property({ type: Number }) value = 0;

  /** Range-mode [start, end] pair. Takes precedence over `value` when range=true. */
  @property({ attribute: false }) values: [number, number] = [20, 80];

  /** Minimum allowed value. */
  @property({ type: Number }) min = 0;

  /** Maximum allowed value. */
  @property({ type: Number }) max = 100;

  /** Step increment — the slider snaps to the nearest multiple of this value. */
  @property({ type: Number }) step = 1;

  /** Layout axis — `'horizontal'` (default) or `'vertical'`. */
  @property({ reflect: true }) orientation: SliderOrientation = 'horizontal';

  /** Enable dual-handle range mode. Use `.values` to set the start/end pair. */
  @property({ type: Boolean, reflect: true }) range = false;

  /** Disables all pointer and keyboard interaction. */
  @property({ type: Boolean, reflect: true }) disabled = false;

  /** Stretch the slider to fill its container width (or height in vertical mode). */
  @property({ type: Boolean, reflect: true }) fluid = false;

  // ── Internal state ────────────────────────────────────────────────────────

  @state() private _dragging: 0 | 1 | null = null;
  @query('.uwc-slider__track') private _trackEl!: HTMLElement;

  // ── Value helpers ─────────────────────────────────────────────────────────

  private get _start() { return this.values[0]; }
  private get _end()   { return this.values[1]; }

  private _pct(val: number): number {
    return ((val - this.min) / (this.max - this.min)) * 100;
  }

  private _clampSnap(raw: number): number {
    const snapped = Math.round((raw - this.min) / this.step) * this.step + this.min;
    return parseFloat(Math.min(this.max, Math.max(this.min, snapped)).toFixed(10));
  }

  private _posToValue(e: PointerEvent): number {
    const rect = this._trackEl.getBoundingClientRect();
    const size = this.orientation === 'vertical' ? rect.height : rect.width;
    if (size === 0) return this.min;
    const raw = this.orientation === 'vertical'
      ? (rect.bottom - e.clientY) / size * (this.max - this.min) + this.min
      : (e.clientX - rect.left)   / size * (this.max - this.min) + this.min;
    return this._clampSnap(raw);
  }

  // ── Track pointer events (captures for track-click drags) ─────────────────

  private _onTrackPointerDown(e: PointerEvent) {
    if (this.disabled) return;
    const target = e.target as HTMLElement;
    if (target.classList.contains('uwc-slider__handle')) return; // handled by handle
    e.preventDefault();
    const newVal = this._posToValue(e);
    if (this.range) {
      const distStart = Math.abs(newVal - this._start);
      const distEnd   = Math.abs(newVal - this._end);
      const idx: 0 | 1 = distStart <= distEnd ? 0 : 1;
      this._setRange(idx, newVal, false);
      this._dragging = idx;
    } else {
      this._setSingle(newVal, false);
      this._dragging = 0;
    }
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }

  private _onTrackPointerMove(e: PointerEvent) {
    if (this._dragging === null || this.disabled) return;
    const newVal = this._posToValue(e);
    this.range ? this._setRange(this._dragging, newVal, false) : this._setSingle(newVal, false);
  }

  private _onTrackPointerUp(_e: PointerEvent) {
    if (this._dragging === null) return;
    this._dragging = null;
    emit(this, 'uwc-slide-end', { value: this.range ? this.values : this.value });
  }

  // ── Handle pointer events (captures for handle drags) ─────────────────────

  private _onHandlePointerDown(e: PointerEvent, idx: 0 | 1) {
    if (this.disabled) return;
    e.preventDefault();
    e.stopPropagation(); // prevent track from also capturing
    this._dragging = idx;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }

  private _onHandlePointerMove(e: PointerEvent, idx: 0 | 1) {
    if (this._dragging !== idx) return;
    const newVal = this._posToValue(e);
    this.range ? this._setRange(idx, newVal, false) : this._setSingle(newVal, false);
  }

  private _onHandlePointerUp(_e: PointerEvent, _idx: 0 | 1) {
    if (this._dragging === null) return;
    this._dragging = null;
    emit(this, 'uwc-slide-end', { value: this.range ? this.values : this.value });
  }

  // ── Keyboard ──────────────────────────────────────────────────────────────

  private _onHandleKeydown(e: KeyboardEvent, idx: 0 | 1) {
    if (this.disabled) return;
    const pageStep = Math.max(this.step, Math.round((this.max - this.min) / 10));
    const current  = this.range ? (idx === 0 ? this._start : this._end) : this.value;
    let next = current;
    let handled = true;

    switch (e.key) {
      case 'ArrowRight': case 'ArrowUp':    next = current + this.step;  break;
      case 'ArrowLeft':  case 'ArrowDown':  next = current - this.step;  break;
      case 'Home':                          next = this.min;              break;
      case 'End':                           next = this.max;              break;
      case 'PageUp':                        next = current + pageStep;    break;
      case 'PageDown':                      next = current - pageStep;    break;
      default: handled = false;
    }

    if (!handled) return;
    e.preventDefault();
    this.range ? this._setRange(idx, next, true) : this._setSingle(next, true);
  }

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  override firstUpdated(): void {
    this._internals.setFormValue(this.range ? JSON.stringify(this.values) : String(this.value));
  }

  // ── Value mutation ────────────────────────────────────────────────────────

  private _setSingle(raw: number, end: boolean) {
    const clamped = this._clampSnap(raw);
    if (clamped === this.value) return;
    this.value = clamped;
    emit(this, 'uwc-change',    { value: clamped });
    if (end) emit(this, 'uwc-slide-end', { value: clamped });
    this._internals.setFormValue(String(clamped));
  }

  private _setRange(idx: 0 | 1, raw: number, end: boolean) {
    const clamped = this._clampSnap(raw);
    const [s, e2] = this.values;
    const next: [number, number] = idx === 0
      ? [Math.min(clamped, e2), e2]
      : [s, Math.max(clamped, s)];
    if (next[0] === s && next[1] === e2) return;
    this.values = next;
    emit(this, 'uwc-change',    { value: next });
    if (end) emit(this, 'uwc-slide-end', { value: next });
    this._internals.setFormValue(JSON.stringify(next));
  }

  // ── Render ────────────────────────────────────────────────────────────────

  override render() {
    const vert     = this.orientation === 'vertical';
    const isRange  = this.range;
    const valPct   = this._pct(this.value);
    const startPct = this._pct(this._start);
    const endPct   = this._pct(this._end);

    // Range fill: covers from start to end (or 0 to val in single mode)
    const rangeStyle = vert
      ? { bottom: `${isRange ? startPct : 0}%`, top: `${100 - (isRange ? endPct : valPct)}%` }
      : { left:   `${isRange ? startPct : 0}%`, right: `${100 - (isRange ? endPct : valPct)}%` };

    // Handle positions
    const h0Style = vert ? { bottom: `${isRange ? startPct : valPct}%` } : { left: `${isRange ? startPct : valPct}%` };
    const h1Style = vert ? { bottom: `${endPct}%` }                      : { left: `${endPct}%` };

    return html`
      <div
        class=${classMap({
          'uwc-slider':          true,
          'uwc-slider--vertical': vert,
          'uwc-slider--range':    isRange,
        })}
        role=${isRange ? 'group' : 'presentation'}
        aria-label=${isRange ? 'Range slider' : nothing as unknown as string}
      >
        <div
          class=${classMap({ 'uwc-slider__track': true, 'is-dragging': this._dragging !== null })}
          @pointerdown=${this._onTrackPointerDown}
          @pointermove=${this._onTrackPointerMove}
          @pointerup=${this._onTrackPointerUp}
          @pointercancel=${this._onTrackPointerUp}
        >
          <div class="uwc-slider__range" style=${styleMap(rangeStyle)}></div>

          <!-- Handle 0: single value or range start -->
          <div
            part="handle"
            class=${classMap({ 'uwc-slider__handle': true, 'is-active': this._dragging === 0 })}
            style=${styleMap(h0Style)}
            role="slider"
            tabindex=${this.disabled ? -1 : 0}
            aria-valuemin=${this.min}
            aria-valuemax=${isRange ? this._end : this.max}
            aria-valuenow=${isRange ? this._start : this.value}
            aria-orientation=${this.orientation}
            aria-label=${isRange ? 'Start value' : 'Value'}
            aria-disabled=${this.disabled}
            @pointerdown=${(e: PointerEvent) => this._onHandlePointerDown(e, 0)}
            @pointermove=${(e: PointerEvent) => this._onHandlePointerMove(e, 0)}
            @pointerup=${(e: PointerEvent)   => this._onHandlePointerUp(e, 0)}
            @pointercancel=${(e: PointerEvent) => this._onHandlePointerUp(e, 0)}
            @keydown=${(e: KeyboardEvent) => this._onHandleKeydown(e, 0)}
          ></div>

          <!-- Handle 1: range end only -->
          ${isRange ? html`
            <div
              part="handle"
              class=${classMap({ 'uwc-slider__handle': true, 'is-active': this._dragging === 1 })}
              style=${styleMap(h1Style)}
              role="slider"
              tabindex=${this.disabled ? -1 : 0}
              aria-valuemin=${this._start}
              aria-valuemax=${this.max}
              aria-valuenow=${this._end}
              aria-orientation=${this.orientation}
              aria-label="End value"
              aria-disabled=${this.disabled}
              @pointerdown=${(e: PointerEvent) => this._onHandlePointerDown(e, 1)}
              @pointermove=${(e: PointerEvent) => this._onHandlePointerMove(e, 1)}
              @pointerup=${(e: PointerEvent)   => this._onHandlePointerUp(e, 1)}
              @pointercancel=${(e: PointerEvent) => this._onHandlePointerUp(e, 1)}
              @keydown=${(e: KeyboardEvent) => this._onHandleKeydown(e, 1)}
            ></div>
          ` : nothing}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-slider': UwcSlider; }
}
